import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import CountUp from "react-countup";
import {
    ArrowLeft,
    Tree,
    Waves,
    BatteryFull,
    Medal,
    Sun,
    ArrowUpRight,
    Lock,
} from "@phosphor-icons/react";
import { Toaster } from "sonner";
import { api, endpoints } from "@/lib/api";

const Stat = ({ icon: Icon, color, label, value, suffix }) => (
    <div className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-5">
        <div className="flex items-center gap-2" style={{ color }}>
            <Icon size={14} weight="fill" />
            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                {label}
            </span>
        </div>
        <div className="mt-3 font-mono-metric text-3xl font-bold text-white sm:text-4xl">
            <CountUp end={value} separator="," duration={1.6} />
            {suffix && (
                <span className="ml-2 text-sm font-normal text-zinc-500">{suffix}</span>
            )}
        </div>
    </div>
);

const PartnerAttribution = () => {
    const { slug = "coaterz" } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let ignore = false;
        api.get(endpoints.partnerAttribution(slug))
            .then((r) => !ignore && setData(r.data))
            .catch(() => !ignore && setError(true));
        return () => {
            ignore = true;
        };
    }, [slug]);

    if (error) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#050505] text-white">
                <div className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-10 text-center">
                    <h1 className="font-editorial text-2xl">Partner not found.</h1>
                    <Link
                        to="/social-kit"
                        className="mt-4 inline-flex items-center gap-2 text-sm text-[#a3e635]"
                    >
                        <ArrowLeft size={14} /> Back to admin
                    </Link>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-[#050505] p-10 text-white">
                <div className="h-96 animate-pulse rounded-lg border border-[#1f1f22] bg-[#0a0a0a]" />
            </div>
        );
    }

    const p = data.partner;
    const t = data.totals;

    return (
        <div
            data-testid="partner-attribution-page"
            className="min-h-screen bg-[#050505] text-white"
        >
            <Toaster theme="dark" position="bottom-right" />
            <header className="border-b border-[#1f1f22] bg-black/70 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-4 sm:px-6 lg:px-10">
                    <Link
                        to="/social-kit"
                        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100"
                    >
                        <ArrowLeft size={14} /> Back to admin
                    </Link>
                    <div className="flex items-center gap-2 text-zinc-500">
                        <Lock size={12} />
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em]">
                            Private · Partner report
                        </span>
                    </div>
                </div>
            </header>

            <main className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 py-10 sm:gap-8 sm:px-6 lg:px-10 lg:py-14">
                {/* Hero */}
                <section className="relative overflow-hidden rounded-lg border border-[#1f1f22] bg-gradient-to-br from-[#0b0f05] via-[#080808] to-[#050b0d] p-6 sm:p-8 lg:p-10">
                    <div className="absolute -top-16 right-0 h-64 w-64 rounded-full bg-[#a3e635]/10 blur-3xl" />
                    <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                        <div>
                            <span className="eviq-chip">Partner Attribution · {data.month}</span>
                            <h1 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl md:text-5xl">
                                What {p.name} funded this month.
                            </h1>
                            <p className="mt-3 max-w-xl text-sm text-zinc-400">
                                A transparent breakdown of trees, cohorts, and ocean-cleanup
                                dollars underwritten by {p.name} — plus a snapshot of where the
                                broader community stands as of today.
                            </p>
                            {p.website_url && (
                                <a
                                    href={p.website_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#a3e635] hover:text-[#c8f37f]"
                                >
                                    Visit {p.name.toLowerCase()}
                                    <ArrowUpRight size={12} weight="bold" />
                                </a>
                            )}
                        </div>
                        <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#27272a] bg-black">
                            <img
                                src={p.logo_url}
                                alt={`${p.name} logo`}
                                className="h-full w-full object-contain p-2"
                            />
                        </div>
                    </div>
                </section>

                {/* Totals */}
                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10">
                    <span className="eviq-chip">Impact this month</span>
                    <h2 className="mt-3 font-editorial text-2xl text-white sm:text-3xl">
                        Amplified by {p.name}.
                    </h2>
                    <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
                        <Stat
                            icon={Tree}
                            color="#a3e635"
                            label="Trees funded"
                            value={t.trees_funded}
                        />
                        <Stat
                            icon={Waves}
                            color="#06b6d4"
                            label="Ocean plastic (lbs)"
                            value={t.ocean_plastic_lbs}
                        />
                        <Stat
                            icon={BatteryFull}
                            color="#8b5cf6"
                            label="Battery-years extended"
                            value={t.battery_years_extended}
                        />
                        <Stat
                            icon={Medal}
                            color="#f59e0b"
                            label="Veteran hours"
                            value={t.veteran_training_hours}
                        />
                        <Stat
                            icon={Sun}
                            color="#f59e0b"
                            label="Solar schools"
                            value={t.solar_schools_supported}
                        />
                    </div>
                </section>

                {/* Projects */}
                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10">
                    <span className="eviq-chip">Projects underwritten</span>
                    <h2 className="mt-3 font-editorial text-2xl text-white sm:text-3xl">
                        Where the dollars landed.
                    </h2>
                    <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
                        {data.projects.map((proj) => (
                            <div
                                key={proj.id}
                                data-testid={`partner-project-${proj.id}`}
                                className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-6"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                        {proj.status}
                                    </span>
                                    <span className="rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-2 py-0.5 font-mono-metric text-[10px] uppercase tracking-[0.2em] text-[#a3e635]">
                                        {proj.contribution_pct}% by {p.name}
                                    </span>
                                </div>
                                <h3 className="mt-3 font-editorial text-lg leading-tight text-white">
                                    {proj.name}
                                </h3>
                                <p className="mt-1 text-xs text-zinc-500">{proj.region}</p>
                                <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-zinc-300">
                                    {proj.trees && (
                                        <span className="rounded border border-[#1f1f22] bg-black px-2 py-1">
                                            {proj.trees.toLocaleString()} trees
                                        </span>
                                    )}
                                    {proj.veteran_hours && (
                                        <span className="rounded border border-[#1f1f22] bg-black px-2 py-1">
                                            {proj.veteran_hours} veteran hours
                                        </span>
                                    )}
                                    {proj.plastic_lbs && (
                                        <span className="rounded border border-[#1f1f22] bg-black px-2 py-1">
                                            {proj.plastic_lbs.toLocaleString()} lbs plastic
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Snapshot */}
                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-8 lg:p-10">
                    <span className="eviq-chip">Community snapshot</span>
                    <h2 className="mt-3 font-editorial text-2xl text-white sm:text-3xl">
                        The bigger picture, right now.
                    </h2>
                    <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <Stat
                            icon={Tree}
                            color="#a3e635"
                            label="Community trees restored"
                            value={data.community_snapshot.community_trees}
                        />
                        <Stat
                            icon={Waves}
                            color="#06b6d4"
                            label="Community ocean plastic (lbs)"
                            value={data.community_snapshot.community_ocean_lbs}
                        />
                    </div>
                </section>
            </main>
        </div>
    );
};

export default PartnerAttribution;
